import { TOKEN } from "@/types/common/ids";
import { GetCache, SetCache } from "@/utils/library/cache";
import { apiFetch } from "@/lib/api";
import { BASE_AUTH } from "./baseUrls";
import { RACCOON_API } from "./constants/routes";

const SERVER_TOKEN_KEY = "authToken:server";
const GALACTUS_SERVER_TOKEN_KEY = "authToken:galactus";
const RACOON_SERVER_TOKEN_KEY = "authToken:racoon";

/** Max wait for Redis cache read; if exceeded, fetch token and skip cache write (Redis may be down or slow). */
export const SERVER_TOKEN_CACHE_GET_TIMEOUT_MS = 2000;

/** Node-only: avoid this handle being the sole reason the process stays alive (no-op in browsers). */
function unrefTimerHandle(handle: ReturnType<typeof setTimeout>): void {
  (handle as { unref?: () => void }).unref?.();
}

export const GetServerToken = async (): Promise<TOKEN> => {
  let cacheGetTimedOut = false;

  try {
    let cacheRaceTimer: ReturnType<typeof setTimeout> | undefined;

    const clearCacheRaceTimer = () => {
      if (cacheRaceTimer !== undefined) {
        clearTimeout(cacheRaceTimer);
        cacheRaceTimer = undefined;
      }
    };

    const cachePromise = GetCache<TOKEN>(SERVER_TOKEN_KEY, undefined)
      .then((cached) => {
        clearCacheRaceTimer();
        return { kind: "resolved" as const, cached };
      })
      .catch((error: unknown) => {
        clearCacheRaceTimer();
        throw error;
      });

    const timeoutPromise = new Promise<{ kind: "timeout" }>((resolve) => {
      cacheRaceTimer = setTimeout(() => {
        cacheRaceTimer = undefined;
        resolve({ kind: "timeout" });
      }, SERVER_TOKEN_CACHE_GET_TIMEOUT_MS);
      if (cacheRaceTimer !== undefined) {
        unrefTimerHandle(cacheRaceTimer);
      }
    });

    const outcome = await Promise.race([cachePromise, timeoutPromise]);

    if (outcome.kind === "timeout") {
      cacheGetTimedOut = true;
    } else if (outcome.cached) {
      return outcome.cached;
    }
  } catch {
    // e.g. REDIS_MISSING_CONFIG from GetCache — proceed without cached token
  }

  //Get Token from Endpoint
  const authResponse = await apiFetch<{
      access_token: TOKEN;
      token_type: string;
      expires_in: number;
    }>(`${BASE_AUTH}/oauth/token`, "POST", { body: {
    grant_type: "client_credentials",
    audience: process.env.AUTH0_AUDIENCE!,
    client_id: process.env.AUTH0_ADMIN_CLIENT_ID!,
    client_secret: process.env.AUTH0_ADMIN_CLIENT_SECRET!,
  } });

  if (!authResponse) {
    throw new Error("Failed to get server token");
  }

  // Save token unless cache read timed out (avoid slow/failed Redis blocking writes)
  if (!cacheGetTimedOut) {
    await SetCache(
      SERVER_TOKEN_KEY,
      authResponse.access_token,
      authResponse.expires_in - 300 // 5 minutes before expiration
    );
  }

  return authResponse.access_token;
};

export const GetGalactusServerToken = async (): Promise<TOKEN> => {
  //Get Cached Token if available
  const cachedToken = await GetCache<TOKEN>(GALACTUS_SERVER_TOKEN_KEY, undefined);

  if (cachedToken) {
    return cachedToken;
  }

  //Get Token from Endpoint
  const authResponse = await apiFetch<{
      access_token: TOKEN;
      token_type: string;
      expires_in: number;
    }>(`${BASE_AUTH}/oauth/token`, "POST", { body: {
    grant_type: "client_credentials",
    audience: process.env.AUTH0_AUDIENCE!,
    client_id: process.env.AUTH0_ADMIN_GALACTUS_CLIENT_ID!,
    client_secret: process.env.AUTH0_ADMIN_GALACTUS_CLIENT_SECRET!,
  } });

  if (!authResponse) {
    throw new Error("Failed to get server token");
  }

  //Save Token to Cache
  await SetCache(
    GALACTUS_SERVER_TOKEN_KEY,
    authResponse.access_token,
    authResponse.expires_in - 300 // 5 minutes before expiration
  );

  return authResponse.access_token;
};

// Needed for Racoon API
export const GetRacoonServerToken = async (): Promise<TOKEN> => {
  //Get Cached Token if available
  const cachedToken = await GetCache<TOKEN>(RACOON_SERVER_TOKEN_KEY, undefined);

  if (cachedToken) {
    return cachedToken;
  }

  //Get Token from Endpoint
  const authResponse = await apiFetch<{
      access_token: TOKEN;
      token_type: string;
      expires_in: number;
    }>(`${BASE_AUTH}/oauth/token`, "POST", { body: {
    grant_type: "client_credentials",
    audience: `${RACCOON_API}`,
    client_id: process.env.AUTH0_ADMIN_GALACTUS_CLIENT_ID!,
    client_secret: process.env.AUTH0_ADMIN_GALACTUS_CLIENT_SECRET!,
  } });

  if (!authResponse) {
    throw new Error("Failed to get server token");
  }

  //Save Token to Cache
  await SetCache(
    RACOON_SERVER_TOKEN_KEY,
    authResponse.access_token,
    authResponse.expires_in - 300 // 5 minutes before expiration
  );

  return authResponse.access_token;
};
